import React, { useState } from 'react';
import { 
  RefreshCw, Clock, Calendar, Shield, Check, 
  AlertTriangle, ArrowLeftRight, Settings
} from 'lucide-react';
import { Button } from '../ui/Button';

interface CrmSyncSettingsProps {
  onSave?: (settings: any) => void;
}

const CrmSyncSettings: React.FC<CrmSyncSettingsProps> = ({ onSave }) => {
  const [syncSettings, setSyncSettings] = useState({
    autoSync: true,
    syncFrequency: 'daily',
    notifyOnSync: true,
    syncFields: {
      contacts: true,
      tasks: true,
      notes: true,
      opportunities: true,
      accounts: true
    },
    conflictResolution: 'newest',
    privacySettings: {
      anonymizeData: false,
      limitDataAccess: true,
      encryptData: true,
      retentionPeriodDays: 365
    }
  });

  const handleSaveSettings = () => {
    if (onSave) {
      onSave(syncSettings);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">CRM Sync Settings</h2>
          <p className="mt-1 text-sm text-gray-500">
            Configure how data is synchronized between AdvisorGrow and your CRM systems
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Settings className="h-5 w-5 text-primary mr-2" />
          Synchronization Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Sync Preferences</h4>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.autoSync}
                    onChange={(e) => setSyncSettings(prev => ({ ...prev, autoSync: e.target.checked }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable automatic sync</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-6">
                  When enabled, data will be automatically synced based on your frequency settings
                </p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Sync Frequency</label>
                <select
                  value={syncSettings.syncFrequency}
                  onChange={(e) => setSyncSettings(prev => ({ 
                    ...prev, 
                    syncFrequency: e.target.value
                  }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                >
                  <option value="realtime">Real-time (immediate sync)</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.notifyOnSync}
                    onChange={(e) => setSyncSettings(prev => ({ ...prev, notifyOnSync: e.target.checked }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Notify me of sync results</span>
                </label>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Data Sync Fields</h4>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.syncFields.contacts}
                    onChange={(e) => setSyncSettings(prev => ({ 
                      ...prev, 
                      syncFields: {
                        ...prev.syncFields,
                        contacts: e.target.checked
                      }
                    }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Contacts</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.syncFields.tasks}
                    onChange={(e) => setSyncSettings(prev => ({ 
                      ...prev, 
                      syncFields: {
                        ...prev.syncFields,
                        tasks: e.target.checked
                      }
                    }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Tasks</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.syncFields.notes}
                    onChange={(e) => setSyncSettings(prev => ({ 
                      ...prev, 
                      syncFields: {
                        ...prev.syncFields,
                        notes: e.target.checked
                      }
                    }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Notes</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.syncFields.opportunities}
                    onChange={(e) => setSyncSettings(prev => ({ 
                      ...prev, 
                      syncFields: {
                        ...prev.syncFields,
                        opportunities: e.target.checked
                      }
                    }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Opportunities</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.syncFields.accounts}
                    onChange={(e) => setSyncSettings(prev => ({ 
                      ...prev, 
                      syncFields: {
                        ...prev.syncFields,
                        accounts: e.target.checked
                      }
                    }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Accounts</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Conflict Resolution</h4>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                checked={syncSettings.conflictResolution === 'newest'}
                onChange={() => setSyncSettings(prev => ({ ...prev, conflictResolution: 'newest' }))}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Use newest version</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={syncSettings.conflictResolution === 'advisorgrow'}
                onChange={() => setSyncSettings(prev => ({ ...prev, conflictResolution: 'advisorgrow' }))}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Prefer AdvisorGrow data</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={syncSettings.conflictResolution === 'crm'}
                onChange={() => setSyncSettings(prev => ({ ...prev, conflictResolution: 'crm' }))}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Prefer CRM data</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={syncSettings.conflictResolution === 'manual'}
                onChange={() => setSyncSettings(prev => ({ ...prev, conflictResolution: 'manual' }))}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Manual resolution (prompt user)</span>
            </label>
          </div>
        </div>
        
        {/* Privacy Settings */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Privacy Settings
          </h4>
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={syncSettings.privacySettings.encryptData}
                onChange={(e) => setSyncSettings(prev => ({ 
                  ...prev, 
                  privacySettings: {
                    ...prev.privacySettings,
                    encryptData: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Encrypt CRM data in transit and at rest</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={syncSettings.privacySettings.limitDataAccess}
                onChange={(e) => setSyncSettings(prev => ({ 
                  ...prev, 
                  privacySettings: {
                    ...prev.privacySettings,
                    limitDataAccess: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Limit data access to authorized users only</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={syncSettings.privacySettings.anonymizeData}
                onChange={(e) => setSyncSettings(prev => ({ 
                  ...prev, 
                  privacySettings: {
                    ...prev.privacySettings,
                    anonymizeData: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Anonymize sensitive client data in reports</span>
            </label>
            
            <div className="mt-2">
              <label className="block text-sm text-gray-700 mb-1">Data Retention Period (days)</label>
              <input
                type="number"
                min="30"
                max="3650"
                value={syncSettings.privacySettings.retentionPeriodDays}
                onChange={(e) => setSyncSettings(prev => ({ 
                  ...prev, 
                  privacySettings: {
                    ...prev.privacySettings,
                    retentionPeriodDays: parseInt(e.target.value)
                  }
                }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSaveSettings}
          >
            <Save className="h-4 w-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </div>
      
      {/* Sync Schedule */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Calendar className="h-5 w-5 text-primary mr-2" />
          Sync Schedule
        </h3>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <Clock className="h-5 w-5 text-primary mr-2" />
                <h4 className="font-medium text-gray-900">Next Scheduled Sync</h4>
              </div>
              <p className="text-gray-600">
                {syncSettings.autoSync 
                  ? syncSettings.syncFrequency === 'realtime'
                    ? 'Real-time sync enabled'
                    : syncSettings.syncFrequency === 'hourly'
                    ? `Next sync at ${new Date(Math.ceil(Date.now() / 3600000) * 3600000).toLocaleTimeString()}`
                    : syncSettings.syncFrequency === 'daily'
                    ? 'Next sync at midnight'
                    : `Next sync on ${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}`
                  : 'Automatic sync is disabled'
                }
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <ArrowLeftRight className="h-5 w-5 text-primary mr-2" />
                <h4 className="font-medium text-gray-900">Sync Direction</h4>
              </div>
              <p className="text-gray-600">
                Bidirectional sync enabled
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center mb-2">
                <AlertTriangle className="h-5 w-5 text-primary mr-2" />
                <h4 className="font-medium text-gray-900">Conflict Resolution</h4>
              </div>
              <p className="text-gray-600 capitalize">
                {syncSettings.conflictResolution === 'advisorgrow' 
                  ? 'Prefer AdvisorGrow data' 
                  : syncSettings.conflictResolution === 'crm'
                  ? 'Prefer CRM data'
                  : syncSettings.conflictResolution === 'manual'
                  ? 'Manual resolution'
                  : 'Use newest version'
                }
              </p>
            </div>
          </div>
          
          <div className="flex justify-center">
            <Button
              variant="outline"
              className="py-1.5 px-3"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Now
            </Button>
          </div>
        </div>
      </div>
      
      {/* Sync History */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Clock className="h-5 w-5 text-primary mr-2" />
          Sync History
        </h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Connection</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records Synced</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Sample sync history entries */}
              {[
                { 
                  timestamp: new Date(Date.now() - 3600000).toISOString(),
                  connection: 'Redtail CRM',
                  status: 'success',
                  recordsSynced: 42,
                  duration: '1.2s'
                },
                { 
                  timestamp: new Date(Date.now() - 86400000).toISOString(),
                  connection: 'Wealthbox CRM',
                  status: 'success',
                  recordsSynced: 18,
                  duration: '0.8s'
                },
                { 
                  timestamp: new Date(Date.now() - 172800000).toISOString(),
                  connection: 'Redtail CRM',
                  status: 'error',
                  recordsSynced: 0,
                  duration: '2.5s',
                  error: 'API rate limit exceeded'
                }
              ].map((entry, index) => (
                <tr key={index}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(entry.timestamp).toLocaleString()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.connection}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      entry.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {entry.status === 'success' ? (
                        <Check className="h-3 w-3 mr-1" />
                      ) : (
                        <AlertTriangle className="h-3 w-3 mr-1" />
                      )}
                      {entry.status}
                    </span>
                    {entry.error && (
                      <p className="text-xs text-red-600 mt-1">{entry.error}</p>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {entry.recordsSynced}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {entry.duration}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CrmSyncSettings;